import { useState, useEffect, useMemo } from 'react';
import { AdditionalItems, Asset, ASSET_TYPE } from '@24i/nxg-sdk-photon/src';
import { DeviceInfo, useDimensions } from '@24i/nxg-sdk-quantum';
import { useImage } from '@24i/nxg-sdk-gluons/src/context/ImageService';
import { ORIENTATIONS } from '@24i/nxg-core-utils/src/constants';
import { BannerCarouselTypes } from '../types';

const useViewModel = (props: BannerCarouselTypes): BannerCarouselTypes => {
    const { section, itemLimit } = props;
    const { getImagesByOrientation } = useImage();
    const [items, setItems] = useState<Asset[] | undefined>(undefined);
    const { isLandscape } = useDimensions();
    const orientation = isLandscape ? ORIENTATIONS.LANDSCAPE : ORIENTATIONS.PORTRAIT;
    const isPortraitPhone = !DeviceInfo.isTablet() && orientation === ORIENTATIONS.PORTRAIT;
    const { label } = section;
    const updatedSection = useMemo(
        () => ({
            ...section,
            additionalItems: getImagesByOrientation(section.additionalItems, orientation),
        }),
        [section, orientation]
    );

    const transformSection = (): void => {
        // We should never display more then 8 assets in the Hero
        const sectionItems = updatedSection.items?.slice(0, Math.min(itemLimit, 8)) || [];
        if (updatedSection.additionalItems && updatedSection.additionalItems.length > 0) {
            const { additionalItems } = updatedSection;
            additionalItems
                .slice()
                .reverse()
                .forEach(
                    (
                        {
                            id,
                            title,
                            description,
                            position,
                            textPlacement,
                            type,
                            target,
                            images,
                            background,
                        }: AdditionalItems,
                        i
                    ) => {
                        const addItem: Asset = {
                            id: id || `${i}`,
                            title,
                            description,
                            background: background || images?.[0].url,
                            textPlacement,
                            type: type as ASSET_TYPE,
                            target,
                        };
                        if (position === 'start') sectionItems.unshift(addItem);
                        if (position === 'end') sectionItems.push(addItem);
                    }
                );
        }
        setItems(sectionItems);
    };

    useEffect(() => {
        transformSection();
    }, [section, isLandscape]);

    return {
        ...props,
        items,
        isPortraitPhone,
        label,
    };
};

export { useViewModel };
